import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { absUrl, api } from "@/lib/api";

const DEFAULT_LOGO = "/logo.jpg";
const DEFAULT_FAVICON = "/favicon.png";
const SiteContext = createContext({ logoUrl: DEFAULT_LOGO, custom: false, marketplace: true, usd: null, refresh: () => {} });

export function SiteProvider({ children }) {
  const [logo, setLogo] = useState(null);
  const [marketplace, setMarketplace] = useState(true);
  const [usd, setUsd] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("fmo_usd")) || null;  // last known USD prices: no EUR flash on first paint
    } catch {
      return null;
    }
  });

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/public/settings");
      setLogo(absUrl(data.logo_url));
      setMarketplace(data.marketplace !== false);
      setUsd(data.usd || null);
      localStorage.setItem("fmo_usd", JSON.stringify(data.usd || null));
    } catch {
      setLogo(null);
    }
  }, []);

  // USD prices follow the live EUR→USD rate (refreshed every 10 min server-side): re-read every 5 min and on focus.
  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 5 * 60 * 1000);
    const onVisible = () => document.visibilityState === "visible" && refresh();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh]);

  useEffect(() => {
    const link = document.getElementById("site-favicon");
    if (link) link.href = logo || DEFAULT_FAVICON;
  }, [logo]);

  return (
    <SiteContext.Provider value={{ logoUrl: logo || DEFAULT_LOGO, custom: Boolean(logo), marketplace, usd, refresh }}>
      {children}
    </SiteContext.Provider>
  );
}

export const useSite = () => useContext(SiteContext);
